/** Canonical IA taxonomy: Suite -> Module -> Capability (optional) -> Screen */

export type NavigationNodeStatus = 'active' | 'planned';

export interface NavigationSuiteDef {
  key: string;
  labelKey: string;
  sortOrder: number;
}

export interface NavigationModuleDef {
  key: string;
  suiteKey: string;
  labelKey: string;
  sortOrder: number;
  status?: NavigationNodeStatus;
}

export interface NavigationCapabilityDef {
  key: string;
  moduleKey: string;
  labelKey: string;
  sortOrder: number;
  status?: NavigationNodeStatus;
}

export const NAVIGATION_SUITES: NavigationSuiteDef[] = [
  { key: 'platform', labelKey: 'nav.suite.platform', sortOrder: 10 },
  { key: 'party-identity', labelKey: 'nav.suite.partyIdentity', sortOrder: 15 },
  { key: 'commercial', labelKey: 'nav.suite.commercial', sortOrder: 20 },
  { key: 'operations', labelKey: 'nav.suite.operations', sortOrder: 30 },
  { key: 'finance', labelKey: 'nav.suite.finance', sortOrder: 40 },
  { key: 'banking', labelKey: 'nav.suite.banking', sortOrder: 42 },
  { key: 'card-switch', labelKey: 'nav.suite.cardSwitch', sortOrder: 43 },
  { key: 'investment', labelKey: 'nav.suite.investment', sortOrder: 44 },
  { key: 'insurance', labelKey: 'nav.suite.insurance', sortOrder: 50 },
  { key: 'people', labelKey: 'nav.suite.people', sortOrder: 60 },
  { key: 'analytics', labelKey: 'nav.suite.analytics', sortOrder: 70 },
  { key: 'smart-city', labelKey: 'nav.suite.smartCity', sortOrder: 75 }
];

export const NAVIGATION_MODULES: NavigationModuleDef[] = [
  { key: 'general', suiteKey: 'platform', labelKey: 'nav.module.general', sortOrder: 0 },
  { key: 'platform-core', suiteKey: 'platform', labelKey: 'shell.module.platformCore', sortOrder: 10 },
  { key: 'subscription-billing', suiteKey: 'platform', labelKey: 'shell.module.subscriptionBilling', sortOrder: 30 },
  { key: 'workflow-bpms', suiteKey: 'platform', labelKey: 'shell.module.workflowBpms', sortOrder: 40 },
  { key: 'dynamic-forms-entities', suiteKey: 'platform', labelKey: 'shell.module.dynamicFormsEntities', sortOrder: 50 },
  { key: 'event-hub', suiteKey: 'platform', labelKey: 'nav.module.eventHub', sortOrder: 55 },
  { key: 'knowledge-hub', suiteKey: 'platform', labelKey: 'nav.module.knowledgeHub', sortOrder: 56 },
  { key: 'iot-hub', suiteKey: 'platform', labelKey: 'nav.module.iotHub', sortOrder: 57 },
  { key: 'digital-twin', suiteKey: 'platform', labelKey: 'nav.module.digitalTwin', sortOrder: 58, status: 'planned' },
  { key: 'platform-integrations', suiteKey: 'platform', labelKey: 'nav.module.platformIntegrations', sortOrder: 59, status: 'planned' },
  { key: 'platform-localization', suiteKey: 'platform', labelKey: 'nav.module.platformLocalization', sortOrder: 60 },
  { key: 'organization-management', suiteKey: 'platform', labelKey: 'nav.module.organizationManagement', sortOrder: 61, status: 'planned' },
  { key: 'platform-ai', suiteKey: 'platform', labelKey: 'nav.module.platformAi', sortOrder: 62, status: 'planned' },
  { key: 'ncx-comms', suiteKey: 'platform', labelKey: 'nav.module.ncxComms', sortOrder: 63, status: 'active' },
  { key: 'ticketing', suiteKey: 'platform', labelKey: 'nav.module.ticketing', sortOrder: 64, status: 'planned' },
  { key: 'notifications', suiteKey: 'platform', labelKey: 'nav.module.notifications', sortOrder: 65 },
  { key: 'theming', suiteKey: 'platform', labelKey: 'nav.module.theming', sortOrder: 66, status: 'planned' },
  { key: 'media-library', suiteKey: 'platform', labelKey: 'nav.module.mediaLibrary', sortOrder: 67, status: 'planned' },
  { key: 'mobile-api', suiteKey: 'platform', labelKey: 'nav.module.mobileApi', sortOrder: 68, status: 'planned' },
  { key: 'social-integrations', suiteKey: 'platform', labelKey: 'nav.module.socialIntegrations', sortOrder: 69, status: 'planned' },

  { key: 'party-hub-customers', suiteKey: 'party-identity', labelKey: 'nav.module.partyHubCustomers', sortOrder: 10 },
  { key: 'party-hub-parties', suiteKey: 'party-identity', labelKey: 'nav.module.partyHubParties', sortOrder: 20 },
  { key: 'party-hub-identity', suiteKey: 'party-identity', labelKey: 'nav.module.partyHubIdentity', sortOrder: 30 },

  { key: 'catalog', suiteKey: 'commercial', labelKey: 'shell.module.catalog', sortOrder: 10 },
  { key: 'universal-sales', suiteKey: 'commercial', labelKey: 'shell.module.universalSales', sortOrder: 20 },
  { key: 'marketplace', suiteKey: 'commercial', labelKey: 'nav.module.marketplace', sortOrder: 30 },
  { key: 'digital-products', suiteKey: 'commercial', labelKey: 'nav.module.digitalProducts', sortOrder: 35 },
  { key: 'subscription-products', suiteKey: 'commercial', labelKey: 'nav.module.subscriptionProducts', sortOrder: 36 },
  { key: 'commerce', suiteKey: 'commercial', labelKey: 'nav.module.commerce', sortOrder: 37 },
  { key: 'regulated-finance', suiteKey: 'commercial', labelKey: 'nav.module.regulatedFinance', sortOrder: 38 },
  { key: 'growth', suiteKey: 'commercial', labelKey: 'nav.module.growth', sortOrder: 40 },

  { key: 'inventory', suiteKey: 'operations', labelKey: 'nav.module.inventory', sortOrder: 10 },
  { key: 'service-operations', suiteKey: 'operations', labelKey: 'nav.module.serviceOperations', sortOrder: 20 },
  { key: 'fixed-assets', suiteKey: 'operations', labelKey: 'nav.module.fixedAssets', sortOrder: 30 },
  { key: 'rental', suiteKey: 'operations', labelKey: 'nav.module.rental', sortOrder: 40 },
  { key: 'hospitality', suiteKey: 'operations', labelKey: 'nav.module.hospitality', sortOrder: 45 },
  { key: 'cmms', suiteKey: 'operations', labelKey: 'nav.module.cmms', sortOrder: 50 },
  { key: 'operations-console', suiteKey: 'operations', labelKey: 'nav.module.operationsConsole', sortOrder: 60 },
  { key: 'shipping', suiteKey: 'operations', labelKey: 'nav.module.shipping', sortOrder: 65, status: 'planned' },
  { key: 'fulfillment', suiteKey: 'operations', labelKey: 'nav.module.fulfillment', sortOrder: 70, status: 'planned' },

  { key: 'financial-core-ufc', suiteKey: 'finance', labelKey: 'shell.module.financialCore', sortOrder: 10 },
  { key: 'payments-wallet-lending', suiteKey: 'finance', labelKey: 'shell.module.payments', sortOrder: 20 },
  { key: 'legacy-payments-cashflow', suiteKey: 'finance', labelKey: 'shell.module.legacyPaymentsCashflow', sortOrder: 50 },
  { key: 'legacy-payments-reports', suiteKey: 'finance', labelKey: 'shell.module.legacyPaymentsReports', sortOrder: 60 },
  { key: 'legacy-accounting-management', suiteKey: 'finance', labelKey: 'shell.module.legacyAccountingManagement', sortOrder: 70 },
  // Retired from nav where launch-track equivalent exists — see navigation-legacy-retirement.ts
  { key: 'legacy-sales-commerce', suiteKey: 'finance', labelKey: 'shell.module.legacySalesCommerce', sortOrder: 30 },
  { key: 'legacy-sales-reports', suiteKey: 'finance', labelKey: 'shell.module.legacySalesReports', sortOrder: 40 },
  { key: 'legacy-lending-suite', suiteKey: 'finance', labelKey: 'shell.module.legacyLendingSuite', sortOrder: 80 },
  { key: 'legacy-transaction-settlement', suiteKey: 'finance', labelKey: 'shell.module.legacyTransactionSettlement', sortOrder: 90 },
  { key: 'legacy-assets-management', suiteKey: 'finance', labelKey: 'shell.module.legacyAssetsManagement', sortOrder: 100 },
  { key: 'tax', suiteKey: 'finance', labelKey: 'nav.module.tax', sortOrder: 105, status: 'planned' },

  { key: 'banking', suiteKey: 'banking', labelKey: 'nav.module.banking', sortOrder: 10, status: 'planned' },
  { key: 'card-switch', suiteKey: 'card-switch', labelKey: 'nav.module.cardSwitch', sortOrder: 10, status: 'planned' },
  { key: 'investment', suiteKey: 'investment', labelKey: 'nav.module.investment', sortOrder: 10, status: 'planned' },

  { key: 'insurance-core', suiteKey: 'insurance', labelKey: 'nav.module.insuranceCore', sortOrder: 10 },

  { key: 'hcm', suiteKey: 'people', labelKey: 'nav.module.hcm', sortOrder: 10, status: 'planned' },
  { key: 'learning', suiteKey: 'people', labelKey: 'nav.module.learning', sortOrder: 20, status: 'planned' },

  { key: 'reporting', suiteKey: 'analytics', labelKey: 'nav.module.reporting', sortOrder: 10, status: 'planned' },
  { key: 'smart-city', suiteKey: 'smart-city', labelKey: 'nav.module.smartCity', sortOrder: 10, status: 'planned' }
];

export const NAVIGATION_CAPABILITIES: NavigationCapabilityDef[] = [
  { key: 'party-commercial', moduleKey: 'party-hub-customers', labelKey: 'nav.capability.partyCommercial', sortOrder: 10 },
  { key: 'party-kind-registry', moduleKey: 'party-hub-parties', labelKey: 'nav.capability.partyKindRegistry', sortOrder: 10 },
  { key: 'party-access-control', moduleKey: 'party-hub-identity', labelKey: 'nav.capability.partyAccessControl', sortOrder: 10 },

  { key: 'catalog-master', moduleKey: 'catalog', labelKey: 'nav.capability.catalogMaster', sortOrder: 10 },
  { key: 'catalog-profiles', moduleKey: 'catalog', labelKey: 'nav.capability.catalogProfiles', sortOrder: 20 },

  { key: 'sales-setup', moduleKey: 'universal-sales', labelKey: 'nav.capability.salesSetup', sortOrder: 10 },
  { key: 'sales-orders', moduleKey: 'universal-sales', labelKey: 'nav.capability.salesOrders', sortOrder: 20 },

  { key: 'marketplace-ops', moduleKey: 'marketplace', labelKey: 'nav.capability.marketplaceOps', sortOrder: 10 },
  { key: 'marketplace-finance', moduleKey: 'marketplace', labelKey: 'nav.capability.marketplaceFinance', sortOrder: 20 },

  { key: 'digital-delivery', moduleKey: 'digital-products', labelKey: 'nav.capability.digitalDelivery', sortOrder: 10 },
  { key: 'subscription-billing', moduleKey: 'subscription-products', labelKey: 'nav.capability.subscriptionBilling', sortOrder: 10 },
  { key: 'unified-commerce', moduleKey: 'commerce', labelKey: 'nav.capability.unifiedCommerce', sortOrder: 10 },
  { key: 'regulated-ops', moduleKey: 'regulated-finance', labelKey: 'nav.capability.regulatedOps', sortOrder: 10 },

  { key: 'growth-crm', moduleKey: 'growth', labelKey: 'nav.capability.growthCrm', sortOrder: 10 },
  { key: 'growth-campaigns', moduleKey: 'growth', labelKey: 'nav.capability.growthCampaigns', sortOrder: 20 },
  { key: 'growth-omnichannel', moduleKey: 'growth', labelKey: 'nav.capability.growthOmnichannel', sortOrder: 30 },
  { key: 'growth-seo', moduleKey: 'growth', labelKey: 'nav.capability.growthSeo', sortOrder: 40 },
  { key: 'growth-intelligence', moduleKey: 'growth', labelKey: 'nav.capability.growthIntelligence', sortOrder: 50 },
  { key: 'growth-agents', moduleKey: 'growth', labelKey: 'nav.capability.growthAgents', sortOrder: 60 },
  { key: 'growth-optimizer', moduleKey: 'growth', labelKey: 'nav.capability.growthOptimizer', sortOrder: 70 },
  { key: 'growth-consent', moduleKey: 'growth', labelKey: 'nav.capability.growthConsent', sortOrder: 80 },
  { key: 'growth-twin', moduleKey: 'growth', labelKey: 'nav.capability.growthTwin', sortOrder: 90 },
  { key: 'growth-marketplace', moduleKey: 'growth', labelKey: 'nav.capability.growthMarketplace', sortOrder: 100 },
  { key: 'growth-revops', moduleKey: 'growth', labelKey: 'nav.capability.growthRevops', sortOrder: 110 },

  { key: 'inventory-warehousing', moduleKey: 'inventory', labelKey: 'nav.capability.inventoryWarehousing', sortOrder: 10 },
  { key: 'inventory-tracking', moduleKey: 'inventory', labelKey: 'nav.capability.inventoryTracking', sortOrder: 20 },
  { key: 'inventory-quality', moduleKey: 'inventory', labelKey: 'nav.capability.inventoryQuality', sortOrder: 30 },
  { key: 'inventory-costing', moduleKey: 'inventory', labelKey: 'nav.capability.inventoryCosting', sortOrder: 40 },

  { key: 'service-orders', moduleKey: 'service-operations', labelKey: 'nav.capability.serviceOrders', sortOrder: 10 },
  { key: 'service-boards', moduleKey: 'service-operations', labelKey: 'nav.capability.serviceBoards', sortOrder: 20 },
  { key: 'service-rma', moduleKey: 'service-operations', labelKey: 'nav.capability.serviceRma', sortOrder: 30 },

  { key: 'assets-register', moduleKey: 'fixed-assets', labelKey: 'nav.capability.assetsRegister', sortOrder: 10 },
  { key: 'rental-operations', moduleKey: 'rental', labelKey: 'nav.capability.rentalOperations', sortOrder: 10 },
  { key: 'hospitality-operations', moduleKey: 'hospitality', labelKey: 'nav.capability.hospitalityOperations', sortOrder: 10 },
  { key: 'cmms-maintenance', moduleKey: 'cmms', labelKey: 'nav.capability.cmmsMaintenance', sortOrder: 10 },
  { key: 'operations-kpis', moduleKey: 'operations-console', labelKey: 'nav.capability.operationsKpis', sortOrder: 10 },
  { key: 'shipping-ops', moduleKey: 'shipping', labelKey: 'nav.capability.shippingOps', sortOrder: 10 },
  { key: 'fulfillment-ops', moduleKey: 'fulfillment', labelKey: 'nav.capability.fulfillmentOps', sortOrder: 10 },

  { key: 'ufc-core', moduleKey: 'financial-core-ufc', labelKey: 'nav.capability.ufcCore', sortOrder: 10 },
  { key: 'ufc-control-center', moduleKey: 'financial-core-ufc', labelKey: 'nav.capability.ufcControlCenter', sortOrder: 20 },

  { key: 'payments-ops', moduleKey: 'payments-wallet-lending', labelKey: 'nav.capability.paymentsOps', sortOrder: 10 },
  { key: 'legacy-commerce', moduleKey: 'legacy-sales-commerce', labelKey: 'nav.capability.legacyCommerce', sortOrder: 10 },
  { key: 'legacy-commerce-reports', moduleKey: 'legacy-sales-reports', labelKey: 'nav.capability.legacyCommerceReports', sortOrder: 10 },
  { key: 'legacy-treasury', moduleKey: 'legacy-payments-cashflow', labelKey: 'nav.capability.legacyTreasury', sortOrder: 10 },
  { key: 'legacy-treasury-reports', moduleKey: 'legacy-payments-reports', labelKey: 'nav.capability.legacyTreasuryReports', sortOrder: 10 },
  { key: 'legacy-accounting', moduleKey: 'legacy-accounting-management', labelKey: 'nav.capability.legacyAccounting', sortOrder: 10 },
  { key: 'legacy-lending', moduleKey: 'legacy-lending-suite', labelKey: 'nav.capability.legacyLending', sortOrder: 10 },
  { key: 'legacy-settlement', moduleKey: 'legacy-transaction-settlement', labelKey: 'nav.capability.legacySettlement', sortOrder: 10 },
  { key: 'legacy-financial-assets', moduleKey: 'legacy-assets-management', labelKey: 'nav.capability.legacyFinancialAssets', sortOrder: 10 },

  { key: 'tax-ops', moduleKey: 'tax', labelKey: 'nav.capability.taxOps', sortOrder: 10 },
  { key: 'banking-customer', moduleKey: 'banking', labelKey: 'nav.capability.bankingCustomer', sortOrder: 10 },
  { key: 'banking-products', moduleKey: 'banking', labelKey: 'nav.capability.bankingProducts', sortOrder: 20 },
  { key: 'banking-accounts', moduleKey: 'banking', labelKey: 'nav.capability.bankingAccounts', sortOrder: 30 },
  { key: 'banking-lending', moduleKey: 'banking', labelKey: 'nav.capability.bankingLending', sortOrder: 40 },
  { key: 'banking-payments', moduleKey: 'banking', labelKey: 'nav.capability.bankingPayments', sortOrder: 50 },
  { key: 'banking-cards', moduleKey: 'banking', labelKey: 'nav.capability.bankingCards', sortOrder: 60 },
  { key: 'banking-treasury', moduleKey: 'banking', labelKey: 'nav.capability.bankingTreasury', sortOrder: 70 },
  { key: 'banking-trade', moduleKey: 'banking', labelKey: 'nav.capability.bankingTrade', sortOrder: 80 },
  { key: 'banking-compliance', moduleKey: 'banking', labelKey: 'nav.capability.bankingCompliance', sortOrder: 90 },
  { key: 'banking-risk', moduleKey: 'banking', labelKey: 'nav.capability.bankingRisk', sortOrder: 100 },
  { key: 'banking-crm', moduleKey: 'banking', labelKey: 'nav.capability.bankingCrm', sortOrder: 110 },
  { key: 'banking-agents', moduleKey: 'banking', labelKey: 'nav.capability.bankingAgents', sortOrder: 120 },

  { key: 'cardswitch-transactions', moduleKey: 'card-switch', labelKey: 'nav.capability.cardSwitchTransactions', sortOrder: 10 },
  { key: 'cardswitch-terminals', moduleKey: 'card-switch', labelKey: 'nav.capability.cardSwitchTerminals', sortOrder: 20 },
  { key: 'cardswitch-clearing', moduleKey: 'card-switch', labelKey: 'nav.capability.cardSwitchClearing', sortOrder: 30 },
  { key: 'cardswitch-disputes', moduleKey: 'card-switch', labelKey: 'nav.capability.cardSwitchDisputes', sortOrder: 40 },
  { key: 'cardswitch-fraud', moduleKey: 'card-switch', labelKey: 'nav.capability.cardSwitchFraud', sortOrder: 50 },
  { key: 'cardswitch-reconciliation', moduleKey: 'card-switch', labelKey: 'nav.capability.cardSwitchReconciliation', sortOrder: 60 },

  { key: 'investment-portfolios', moduleKey: 'investment', labelKey: 'nav.capability.investmentPortfolios', sortOrder: 10 },
  { key: 'investment-assets', moduleKey: 'investment', labelKey: 'nav.capability.investmentAssets', sortOrder: 20 },
  { key: 'investment-positions', moduleKey: 'investment', labelKey: 'nav.capability.investmentPositions', sortOrder: 30 },
  { key: 'investment-valuation', moduleKey: 'investment', labelKey: 'nav.capability.investmentValuation', sortOrder: 40 },
  { key: 'investment-performance', moduleKey: 'investment', labelKey: 'nav.capability.investmentPerformance', sortOrder: 50 },
  { key: 'investment-risk', moduleKey: 'investment', labelKey: 'nav.capability.investmentRisk', sortOrder: 60 },
  { key: 'investment-funds', moduleKey: 'investment', labelKey: 'nav.capability.investmentFunds', sortOrder: 70 },
  { key: 'investment-ma', moduleKey: 'investment', labelKey: 'nav.capability.investmentMa', sortOrder: 80 },

  { key: 'insurance-pas', moduleKey: 'insurance-core', labelKey: 'nav.capability.insurancePas', sortOrder: 10 },
  { key: 'insurance-quote-proposal-bind', moduleKey: 'insurance-core', labelKey: 'nav.capability.insuranceQuoteProposal', sortOrder: 15 },
  { key: 'insurance-underwriting', moduleKey: 'insurance-core', labelKey: 'nav.capability.insuranceUnderwriting', sortOrder: 20 },
  { key: 'insurance-claims', moduleKey: 'insurance-core', labelKey: 'nav.capability.insuranceClaims', sortOrder: 25 },
  { key: 'insurance-distribution', moduleKey: 'insurance-core', labelKey: 'nav.capability.insuranceDistribution', sortOrder: 30 },
  { key: 'insurance-commissions', moduleKey: 'insurance-core', labelKey: 'nav.capability.insuranceCommissions', sortOrder: 35 },
  { key: 'insurance-premium-billing', moduleKey: 'insurance-core', labelKey: 'nav.capability.insurancePremiumBilling', sortOrder: 40 },
  { key: 'insurance-health-provider-network', moduleKey: 'insurance-core', labelKey: 'nav.capability.insuranceHealth', sortOrder: 45 },
  { key: 'insurance-group-master-policies', moduleKey: 'insurance-core', labelKey: 'nav.capability.insuranceGroup', sortOrder: 50 },
  { key: 'insurance-reinsurance', moduleKey: 'insurance-core', labelKey: 'nav.capability.insuranceReinsurance', sortOrder: 55 },
  { key: 'insurance-regulatory-adapters', moduleKey: 'insurance-core', labelKey: 'nav.capability.insuranceRegulatory', sortOrder: 60 },

  { key: 'hcm-core-hr', moduleKey: 'hcm', labelKey: 'nav.capability.hcmCoreHr', sortOrder: 10 },
  { key: 'hcm-job-architecture', moduleKey: 'hcm', labelKey: 'nav.capability.hcmJobArchitecture', sortOrder: 20 },
  { key: 'hcm-recruitment', moduleKey: 'hcm', labelKey: 'nav.capability.hcmRecruitment', sortOrder: 30 },
  { key: 'hcm-decrees', moduleKey: 'hcm', labelKey: 'nav.capability.hcmDecrees', sortOrder: 40 },
  { key: 'hcm-time-attendance', moduleKey: 'hcm', labelKey: 'nav.capability.hcmTimeAttendance', sortOrder: 50 },
  { key: 'hcm-leave', moduleKey: 'hcm', labelKey: 'nav.capability.hcmLeave', sortOrder: 60 },
  { key: 'hcm-payroll', moduleKey: 'hcm', labelKey: 'nav.capability.hcmPayroll', sortOrder: 70 },
  { key: 'hcm-compensation', moduleKey: 'hcm', labelKey: 'nav.capability.hcmCompensation', sortOrder: 80 },
  { key: 'hcm-statutory', moduleKey: 'hcm', labelKey: 'nav.capability.hcmStatutory', sortOrder: 90 },
  { key: 'hcm-analytics', moduleKey: 'hcm', labelKey: 'nav.capability.hcmAnalytics', sortOrder: 100 },
  { key: 'hcm-agents', moduleKey: 'hcm', labelKey: 'nav.capability.hcmAgents', sortOrder: 110 },

  { key: 'learning-academic', moduleKey: 'learning', labelKey: 'nav.capability.learningAcademic', sortOrder: 10 },
  { key: 'learning-content', moduleKey: 'learning', labelKey: 'nav.capability.learningContent', sortOrder: 20 },
  { key: 'learning-lms', moduleKey: 'learning', labelKey: 'nav.capability.learningLms', sortOrder: 30 },
  { key: 'learning-assessment', moduleKey: 'learning', labelKey: 'nav.capability.learningAssessment', sortOrder: 40 },
  { key: 'learning-grading', moduleKey: 'learning', labelKey: 'nav.capability.learningGrading', sortOrder: 50 },
  { key: 'learning-certification', moduleKey: 'learning', labelKey: 'nav.capability.learningCertification', sortOrder: 60 },
  { key: 'learning-lxp', moduleKey: 'learning', labelKey: 'nav.capability.learningLxp', sortOrder: 70 },
  { key: 'learning-skills', moduleKey: 'learning', labelKey: 'nav.capability.learningSkills', sortOrder: 80 },
  { key: 'learning-intelligence', moduleKey: 'learning', labelKey: 'nav.capability.learningIntelligence', sortOrder: 90 },
  { key: 'learning-career', moduleKey: 'learning', labelKey: 'nav.capability.learningCareer', sortOrder: 100 },
  { key: 'learning-agents', moduleKey: 'learning', labelKey: 'nav.capability.learningAgents', sortOrder: 110 },

  { key: 'reporting-dashboards', moduleKey: 'reporting', labelKey: 'nav.capability.reportingDashboards', sortOrder: 10 },
  { key: 'reporting-reports', moduleKey: 'reporting', labelKey: 'nav.capability.reportingReports', sortOrder: 20 },

  { key: 'smart-city-coc', moduleKey: 'smart-city', labelKey: 'nav.capability.smartCityCoc', sortOrder: 10 },
  { key: 'smart-city-mobility', moduleKey: 'smart-city', labelKey: 'nav.capability.smartCityMobility', sortOrder: 20 },
  { key: 'smart-city-utilities', moduleKey: 'smart-city', labelKey: 'nav.capability.smartCityUtilities', sortOrder: 30 },
  { key: 'smart-city-safety', moduleKey: 'smart-city', labelKey: 'nav.capability.smartCitySafety', sortOrder: 40 },
  { key: 'smart-city-environment', moduleKey: 'smart-city', labelKey: 'nav.capability.smartCityEnvironment', sortOrder: 50 },
  { key: 'smart-city-buildings', moduleKey: 'smart-city', labelKey: 'nav.capability.smartCityBuildings', sortOrder: 60 },
  { key: 'smart-city-egov', moduleKey: 'smart-city', labelKey: 'nav.capability.smartCityEgov', sortOrder: 70 },
  { key: 'smart-city-open-data', moduleKey: 'smart-city', labelKey: 'nav.capability.smartCityOpenData', sortOrder: 80 },

  { key: 'event-catalog', moduleKey: 'event-hub', labelKey: 'nav.capability.eventCatalog', sortOrder: 10 },
  { key: 'event-webhooks', moduleKey: 'event-hub', labelKey: 'nav.capability.eventWebhooks', sortOrder: 20 },
  { key: 'event-observability', moduleKey: 'event-hub', labelKey: 'nav.capability.eventObservability', sortOrder: 30 },
  { key: 'event-subscriptions', moduleKey: 'event-hub', labelKey: 'nav.capability.eventSubscriptions', sortOrder: 40 },

  { key: 'knowledge-manager', moduleKey: 'knowledge-hub', labelKey: 'nav.capability.knowledgeManager', sortOrder: 10 },
  { key: 'knowledge-ingestion', moduleKey: 'knowledge-hub', labelKey: 'nav.capability.knowledgeIngestion', sortOrder: 20 },
  { key: 'knowledge-governance', moduleKey: 'knowledge-hub', labelKey: 'nav.capability.knowledgeGovernance', sortOrder: 30 },

  { key: 'iot-device-console', moduleKey: 'iot-hub', labelKey: 'nav.capability.iotDeviceConsole', sortOrder: 10 },
  { key: 'iot-alerts', moduleKey: 'iot-hub', labelKey: 'nav.capability.iotAlerts', sortOrder: 20 },
  { key: 'iot-rules', moduleKey: 'iot-hub', labelKey: 'nav.capability.iotRules', sortOrder: 30 },

  { key: 'digital-twin-types', moduleKey: 'digital-twin', labelKey: 'nav.capability.digitalTwinTypes', sortOrder: 10 },
  { key: 'digital-twin-instances', moduleKey: 'digital-twin', labelKey: 'nav.capability.digitalTwinInstances', sortOrder: 20 },

  { key: 'platform-api-keys', moduleKey: 'platform-integrations', labelKey: 'nav.capability.platformApiKeys', sortOrder: 10 },
  { key: 'platform-webhooks', moduleKey: 'platform-integrations', labelKey: 'nav.capability.platformWebhooks', sortOrder: 20 },
  { key: 'platform-connectors', moduleKey: 'platform-integrations', labelKey: 'nav.capability.platformConnectors', sortOrder: 30 },

  { key: 'platform-languages', moduleKey: 'platform-localization', labelKey: 'nav.capability.platformLanguages', sortOrder: 10 },
  { key: 'platform-resources', moduleKey: 'platform-localization', labelKey: 'nav.capability.platformResources', sortOrder: 20 },
  { key: 'platform-content', moduleKey: 'platform-localization', labelKey: 'nav.capability.platformContent', sortOrder: 30 },

  { key: 'org-structure', moduleKey: 'organization-management', labelKey: 'nav.capability.orgStructure', sortOrder: 10 },

  { key: 'platform-ai-settings', moduleKey: 'platform-ai', labelKey: 'nav.capability.platformAiSettings', sortOrder: 10 },

  { key: 'subscription-plans', moduleKey: 'subscription-billing', labelKey: 'nav.capability.subscriptionPlans', sortOrder: 10 },

  { key: 'ncx-platform-admin', moduleKey: 'ncx-comms', labelKey: 'nav.capability.ncxPlatformAdmin', sortOrder: 5 },
  { key: 'ncx-ops', moduleKey: 'ncx-comms', labelKey: 'nav.capability.ncxOps', sortOrder: 10 },
  { key: 'ticketing-ops', moduleKey: 'ticketing', labelKey: 'nav.capability.ticketingOps', sortOrder: 10 },
  { key: 'notifications-ops', moduleKey: 'notifications', labelKey: 'nav.capability.notificationsOps', sortOrder: 10 },
  { key: 'theming-ops', moduleKey: 'theming', labelKey: 'nav.capability.themingOps', sortOrder: 10 },
  { key: 'media-ops', moduleKey: 'media-library', labelKey: 'nav.capability.mediaOps', sortOrder: 10 },
  { key: 'mobile-api-ops', moduleKey: 'mobile-api', labelKey: 'nav.capability.mobileApiOps', sortOrder: 10 },
  { key: 'social-ops', moduleKey: 'social-integrations', labelKey: 'nav.capability.socialOps', sortOrder: 10 }
];

/** Maps legacy API moduleKey to canonical module key */
export const LEGACY_MODULE_KEY_MAP: Record<string, string> = {
  general: 'general',
  'platform-core': 'platform-core',
  'identity-access': 'party-hub-identity',
  'party-hub-customers': 'party-hub-customers',
  'party-hub-parties': 'party-hub-parties',
  'party-hub-identity': 'party-hub-identity',
  'subscription-billing': 'subscription-billing',
  catalog: 'catalog',
  'universal-catalog': 'catalog',
  'universal-sales': 'universal-sales',
  'workflow-bpms': 'workflow-bpms',
  'dynamic-forms-entities': 'dynamic-forms-entities',
  'financial-core-ufc': 'financial-core-ufc',
  'payments-wallet-lending': 'payments-wallet-lending',
  'legacy-sales-commerce': 'legacy-sales-commerce',
  'legacy-sales-reports': 'legacy-sales-reports',
  'legacy-payments-cashflow': 'legacy-payments-cashflow',
  'legacy-payments-reports': 'legacy-payments-reports',
  'legacy-accounting-management': 'legacy-accounting-management',
  'legacy-lending-suite': 'legacy-lending-suite',
  'legacy-transaction-settlement': 'legacy-transaction-settlement',
  'legacy-assets-management': 'legacy-assets-management',
  'insurance-core': 'insurance-core',
  inventory: 'inventory',
  'service-operations': 'service-operations',
  'fixed-assets': 'fixed-assets',
  rental: 'rental',
  hospitality: 'hospitality',
  cmms: 'cmms',
  'operations-console': 'operations-console',
  marketplace: 'marketplace',
  'digital-products': 'digital-products',
  'subscription-products': 'subscription-products',
  commerce: 'commerce',
  'regulated-finance': 'regulated-finance',
  growth: 'growth',
  'growth-platform': 'growth',
  crm: 'growth',
  'event-hub': 'event-hub',
  'knowledge-hub': 'knowledge-hub',
  'iot-hub': 'iot-hub',
  'digital-twin': 'digital-twin',
  hcm: 'hcm',
  learning: 'learning',
  banking: 'banking',
  'card-switch': 'card-switch',
  investment: 'investment',
  'smart-city': 'smart-city',
  shipping: 'shipping',
  fulfillment: 'fulfillment',
  tax: 'tax',
  'call-center': 'ncx-comms',
  'comms-core': 'ncx-comms',
  'ncx-comms': 'ncx-comms',
  ticketing: 'ticketing',
  notifications: 'notifications',
  'notification-messaging': 'notifications',
  theming: 'theming',
  'media-library': 'media-library',
  'mobile-api': 'mobile-api',
  'social-integrations': 'social-integrations',
  'platform-integrations': 'platform-integrations',
  'platform-localization': 'platform-localization',
  'organization-management': 'organization-management',
  'platform-ai': 'platform-ai',
  reporting: 'reporting',
  'reporting-analytics': 'reporting'
};
